import { ref } from 'vue';
import { defineStore } from 'pinia';
import ColorRange from '@connx/color-range';
import cssVarToHex from '@/lib/css-var-to-hex';
import { gridThemeMap, accordionThemeMap, navigationThemeMap } from '@/lib/system-themes';

import useAxiosInstance from '@/composables/axiosInstance';
import useUISessionStore from '../ui-session';

interface CustomThemeSetting {
  variable: string;
  value: string;
  label: string;
}

interface State {
  defaultCssMap: Map<string, string> | null,
  gridTheme: string,
  accordionTheme: string,
  navigationTheme: string,
  navigationBackground: string;
  navigationBackgroundHover: string;
  navigationText: string;
  navigationLogoFill: string;
  buttonText: string;
  headersText: string;
  accordionText: string;
  mainHeaderText: string;
  secondaryHeaderText: string;
  primary: string;
  secondary: string;
  headers: string;
  accordion: string;
}

export interface RGB {
  red: number;
  green: number;
  blue: number;
}

interface ColuorCustomisationApiResponse extends Omit<State, 'grey'> { }

export interface ColorRangeObject {
  name: string;
  hex: string;
  rgb: RGB;
}

const defaultState = () => ({
  defaultCssMap: null,
  gridTheme: getComputedStyle(document.documentElement).getPropertyValue('--connx-grid-theme'),
  gridThemeMap: [],
  accordionTheme: getComputedStyle(document.documentElement).getPropertyValue('--connx-accordion-theme'),
  accordionThemeMap: [],
  navigationTheme: getComputedStyle(document.documentElement).getPropertyValue('--connx-navigation-theme'),
  navigationThemeMap: [],
  navigationBackground: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-navigation-background')),
  navigationBackgroundHover: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-navigation-background-hover')),
  navigationLogoFill: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-navigation-logo-fill')),
  navigationText: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-navigation-text')),
  headersText: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-grid-heading-text')),
  accordionText: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-accordion-heading-text')),
  mainHeaderText: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-main-header')),
  secondaryHeaderText: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-secondary-header')),
  buttonText: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-button-text')),
  primary: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-primary')),
  secondary: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-secondary')),
  headers: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-headers')),
  accordion: cssVarToHex(getComputedStyle(document.documentElement).getPropertyValue('--connx-accordion')),
});

const generateShades = (name: string, color: string): ColorRangeObject[] => {
  const range = new ColorRange(color);
  return [...range.shades.entries()].map((value, index) => {
    const percent = (index * 5);
    const variableName = `${name}-shade-${percent < 10 ? 0 : ''}${percent}`;
    return {
      name: variableName,
      hex: `${value[1]}`,
      rgb: ColorRange.convertHexToRGB(value[1]),
    };
  }).filter((i) => !i.name.includes('00'));
};

const generateTints = (name: string, color: string): ColorRangeObject[] => {
  const range = new ColorRange(color);
  return [...range.tints.entries()].map((value, index) => {
    const percent = (index * 5);
    const variableName = `${name}-tint-${percent < 10 ? 0 : ''}${percent}`;
    return {
      name: variableName,
      hex: `${value[1]}`,
      rgb: ColorRange.convertHexToRGB(value[1]),
    };
  }).filter((i) => !i.name.includes('00'));
};

const useColourCustomisationstore = defineStore({
  id: 'color-customisations',
  state: () => ref<State>({ ...defaultState() }),
  getters: {
    primaryShades(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.primary)) {
        return generateShades('primary', state.primary);
      }
      return [];
    },
    primaryTints(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.primary)) {
        return generateTints('primary', state.primary);
      }
      return [];
    },
    secondaryShades(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.secondary)) {
        return generateShades('secondary', state.secondary);
      }
      return [];
    },
    secondaryTints(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.secondary)) {
        return generateTints('secondary', state.secondary);
      }
      return [];
    },
    headerShades(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.headers)) {
        return generateShades('headers', state.headers);
      }
      return [];
    },
    headerTints(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.headers)) {
        return generateTints('headers', state.headers);
      }
      return [];
    },
    accordionShades(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.accordion)) {
        return generateShades('accordion', state.accordion);
      }
      return [];
    },
    accordionTints(state): ColorRangeObject[] {
      if (ColorRange.isValidHexColor(state.accordion)) {
        return generateTints('accordion', state.accordion);
      }
      return [];
    },
    cssVariableMap(state) {
      const primaryRGB = ColorRange.convertHexToRGB(state.primary);
      const secondaryRGB = ColorRange.convertHexToRGB(state.secondary);
      const headersRGB = ColorRange.convertHexToRGB(state.headers);
      const accordionRGB = ColorRange.convertHexToRGB(state.accordion);

      // @ts-expect-error
      const cssVariableList: [string[]] = [
        ['--connx-primary', state.primary],
        ...this.primaryShades.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ...this.primaryTints.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ['--connx-secondary', state.secondary],
        ...this.secondaryShades.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ...this.secondaryTints.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ['--connx-headers', state.headers],
        ...this.headerShades.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ...this.headerTints.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ['--connx-accordion', state.accordion],
        ...this.accordionShades.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ...this.accordionTints.map(({ name, hex }) => [`--connx-${name}`, `${hex}`]),
        ['--connx-primary-rgb', `${primaryRGB.red} ${primaryRGB.green} ${primaryRGB.blue}`],
        ['--connx-secondary-rgb', `${secondaryRGB.red} ${secondaryRGB.green} ${secondaryRGB.blue}`],
        ['--connx-headers-rgb', `${headersRGB.red} ${headersRGB.green} ${headersRGB.blue}`],
        ['--connx-accordion-rgb', `${accordionRGB.red} ${accordionRGB.green} ${accordionRGB.blue}`],
        ['--connx-button-text', `${state.buttonText}`],
        ['--connx-grid-heading-text', `${state.headersText}`],
        ['--connx-accordion-heading-text', `${state.accordionText}`],
        ['--connx-main-header', state.mainHeaderText],
        ['--connx-secondary-header', state.secondaryHeaderText],
        ['--connx-grid-theme', `${state.gridTheme}`],
        ['--connx-accordion-theme', `${state.accordionTheme}`],
        ['--connx-navigation-theme', `${state.navigationTheme}`],
      ];
      if (gridThemeMap.has(state.gridTheme)) {
        const gridThemeValues: string[][] = gridThemeMap.get(state.gridTheme)!;
        cssVariableList.push(...gridThemeValues)
      } else {
        cssVariableList.push(...gridThemeMap.get('default')!)
      }
      // accordion
      if (accordionThemeMap.has(state.accordionTheme)) {
        const accordionThemeValues: string[][] = accordionThemeMap.get(state.accordionTheme)!;
        cssVariableList.push(...accordionThemeValues)
      } else {
        cssVariableList.push(...accordionThemeMap.get('default')!)
      }
      if (navigationThemeMap.has(state.navigationTheme) && state.navigationTheme !== 'default') {
        const navigationThemeValues: string[][] = navigationThemeMap.get(state.navigationTheme)!;
        cssVariableList.push(...navigationThemeValues)
      } else {
        cssVariableList.push(
          ['--connx-navigation-background', `${state.navigationBackground}`],
          ['--connx-navigation-background-hover', `${state.navigationBackgroundHover}`],
          ['--connx-navigation-text', `${state.navigationText}`],
          ['--connx-navigation-text-hover', `${state.navigationText}`],
          ['--connx-navigation-logo-fill', `${state.navigationLogoFill}`],
        )
      }
      //@ts-ignore
      return new Map(cssVariableList);
      // return cssVariableList;
    },
  },
  actions: {
    generatedStylesheet(): string {
      const generatedStyles: string[] = [
        ':root {',
      ];

      for (const [key, value] of this.cssVariableMap.entries()) {
        generatedStyles.push(`${key}: ${value};`);
      }

      generatedStyles.push('}');

      return generatedStyles.join().replaceAll(',', '');
    },

    async loadDefaultCssVariables() {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const [defaults, client] = await [...document.styleSheets].filter((sheet) => {
            const parts = sheet.href?.split('.css');
            if (parts) {
              const url = parts[0].split('/');
              const sheetName = url[url.length - 1];
              return ['default-variables', 'client-variables'].includes(sheetName);
            }
            return false;
          });

          const vars = client && client.cssRules[0] ? client : defaults;

          const cssVariables = vars.cssRules[0].cssText.replace(':root {', '').replace('}', '').replaceAll(' ', '').split(';');
          const filteredVariables = cssVariables.filter((rule) => {

            if (rule.includes('connx-primary')) {
              return true;
            }

            if (rule.includes('navigation')) {
              return true;
            }

            if (rule.includes('connx-grid')) {
              return true;
            }

            if (rule.includes('connx-accordion')) {
              return true;
            }

            if (rule.includes('headers-text')) {
              return true;
            }

            return false;
          });

          this.defaultCssMap = new Map(filteredVariables.map(((variable) => {
            const obj = variable.split(':');
            return ([obj[0], obj[1]]);
          })));

          if (this.defaultCssMap.has('--connx-primary')) {
            this.primary = cssVarToHex(this.defaultCssMap.get('--connx-primary')!);
          }

          if (this.defaultCssMap.has('--connx-navigation-background')) {
            this.navigationBackground = cssVarToHex(this.defaultCssMap.get('--connx-navigation-background')!);
          }
          if (this.defaultCssMap.has('--connx-navigation-background-hover')) {
            this.navigationBackgroundHover = cssVarToHex(this.defaultCssMap.get('--connx-navigation-background-hover')!);
          }
          if (this.defaultCssMap.has('--connx-navigation-text')) {
            this.navigationText = cssVarToHex(this.defaultCssMap.get('--connx-navigation-text')!);
          }
          if (this.defaultCssMap.has('--connx-navigation-logo-fill')) {
            this.navigationLogoFill = cssVarToHex(this.defaultCssMap.get('--connx-navigation-logo-fill')!);
          }
          if (this.defaultCssMap.has('--connx-grid-heading-text')) {
            this.headersText = cssVarToHex(this.defaultCssMap.get('--connx-grid-heading-text')!);
          }
          if (this.defaultCssMap.has('--connx-accordion-heading-text')) {
            this.accordionText = cssVarToHex(this.defaultCssMap.get('--connx-accordion-heading-text')!);
          }
          if (this.defaultCssMap.has('--connx-grid-theme')) {
            this.gridTheme = this.defaultCssMap.get('--connx-grid-theme')!;
          }
          if (this.defaultCssMap.has('--connx-accordion-theme')) {
            this.accordionTheme = this.defaultCssMap.get('--connx-accordion-theme')!;
          }
          if (this.defaultCssMap.has('--connx-navigation-theme')) {
            this.navigationTheme = this.defaultCssMap.get('--connx-navigation-theme')!;
          }

          resolve(true);
        }, 750);
      })
    },
    setNavigationBackground(hex: string) {
      this.navigationBackground = hex;
    },
    toRGB(value: string) {
      let hex = value;
      if (value.includes('var')) {
        const property = value.substring(
          value.indexOf('(') + 1,
          value.lastIndexOf(')'),
        );
        hex = getComputedStyle(document.documentElement).getPropertyValue(property);
      }
      return ColorRange.convertHexToRGB(hex);
    },
    hexContrastRating(a: string, b: string): number {
      return ColorRange.hexContrastRating(a, b);
    },
    async resetStyleRefrences() {
      const links = document.getElementsByTagName('link');
      await new Promise((resolve) => {
        const link = [...links].find((link) => link.href.includes('client-variables.css'));
        if (link) {
          link.href = `${link.href}?ver=${(new Date()).getTime()}`;
        }
        setTimeout(() => resolve(true), 250);
      });
      await this.loadDefaultCssVariables();
    },
    async submitChanges(): Promise<Boolean> {
      const uiStore = useUISessionStore();
      try {

        await useAxiosInstance.post<ColuorCustomisationApiResponse>('/api/ColourCustomisations/Update', {
          CssContent: this.generatedStylesheet(),
          updatedByEmpCode: uiStore.user.empCode,
        });

        await this.resetStyleRefrences();
        return true;
      } catch (error: any) {
        console.error(error);
      }
      return false;
    },
    async generateColorRange(color: string) {
      interface ColorRange {
        name: string;
        color: string;
      }

      const map = new Map<string, ColorRange>();
      const range = new ColorRange(color);
      for await (const [index, value] of range.shades.entries()) {
        const percent = (index * 5);
        // eslint-disable-next-line no-continue
        if (percent === 0 || percent === 100) continue;
        const variableName: string = `shade-${percent < 10 ? 0 : ''}${percent}`;

        map.set(variableName, {
          name: variableName, color: value,
        });
      }
      for await (const [index, value] of range.tints.entries()) {
        const percent = (index * 5);
        // eslint-disable-next-line no-continue
        if (percent === 0 || percent === 100) continue;
        const variableName: string = `tint-${percent < 10 ? 0 : ''}${percent}`;

        map.set(variableName, {
          name: variableName, color: value,
        });
      }

      return map;
    },
    async resetToDefault(): Promise<Boolean> {
      try {
        await useAxiosInstance.post<ColuorCustomisationApiResponse>('/api/ColourCustomisations/Update', {
          CssContent: '',
        });
        await this.resetStyleRefrences();

        // @ts-ignore
        this.$state = {
          ...defaultState(),
        };
        return true;

      } catch (error: any) {
        console.error(error);
      }
      return false;
    },
  },
  debounce: {
  },
});

export default useColourCustomisationstore;
